import React, { useLayoutEffect, useRef, useState } from 'react';
import { useAppearance } from '../contexts/AppearanceContext';
import './scroll-edge-frame.css';

// Opt-in decoration around an existing viewport. It never scrolls, changes
// pagination, replaces refs or interferes with a virtualized list's handlers.
export default function ScrollEdgeFrame({ children, style, className = '', isDark, viewportSelector = '[data-scroll-edge-viewport]', topEdgeSelector, axis = 'vertical' }) {
  const appearance = useAppearance();
  const frameRef = useRef(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });
  useLayoutEffect(() => {
    const frame = frameRef.current;
    let viewport;
    let scheduled;
    const measure = () => {
      const next = frame.querySelector(viewportSelector);
      if (next !== viewport) {
        resize?.disconnect();
        viewport = next;
        if (viewport) { resize?.observe(viewport); Array.from(viewport.children).forEach(child => resize?.observe(child)); }
      }
      const top = !!viewport && (axis === 'horizontal' ? viewport.scrollLeft : viewport.scrollTop) > 0;
      const bottom = !!viewport && (axis === 'horizontal'
        ? viewport.scrollWidth - viewport.clientWidth - viewport.scrollLeft
        : viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop) > 1;
      const anchor = topEdgeSelector ? frame.querySelector(topEdgeSelector) : null;
      let offset = 0;
      if (anchor) {
        resize?.observe(anchor);
        const frameRect = frame.getBoundingClientRect(), anchorRect = anchor.getBoundingClientRect();
        if (anchorRect.top >= frameRect.top - 1 && anchorRect.bottom <= frameRect.bottom) offset = Math.max(0, anchorRect.bottom - frameRect.top);
      }
      frame.style.setProperty('--scroll-edge-top', `${offset}px`);
      setEdges(current => current.top === top && current.bottom === bottom ? current : { top, bottom });
    };
    const schedule = () => { if (scheduled === undefined) scheduled = requestAnimationFrame(() => { scheduled = undefined; measure(); }); };
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    const mutations = new MutationObserver(schedule);
    mutations.observe(frame, { childList: true, subtree: true });
    frame.addEventListener('scroll', measure, true);
    frame.addEventListener('load', schedule, true);
    window.addEventListener('resize', schedule);
    measure();
    return () => {
      if (scheduled !== undefined) cancelAnimationFrame(scheduled);
      mutations.disconnect(); resize?.disconnect();
      frame.removeEventListener('scroll', measure, true);
      frame.removeEventListener('load', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [viewportSelector, topEdgeSelector, axis]);
  const horizontal = axis === 'horizontal';
  return <div ref={frameRef} className={`scroll-edge-frame ${className}`} style={style} data-shadow-top={!horizontal && edges.top} data-shadow-bottom={!horizontal && edges.bottom} data-shadow-left={horizontal && edges.top} data-shadow-right={horizontal && edges.bottom} data-edge-theme={(isDark ?? appearance.isDark) ? 'dark' : 'light'}>
    {children}
    <span className={`scroll-edge-shadow ${horizontal ? 'is-left' : 'is-top'}`} data-visible={edges.top} aria-hidden="true" />
    <span className={`scroll-edge-shadow ${horizontal ? 'is-right' : 'is-bottom'}`} data-visible={edges.bottom} aria-hidden="true" />
  </div>;
}
